import { useState } from "react";
import {
  Button,
  MessageBar,
  MessageBarBody,
  Spinner,
  Tab,
  TabList,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  EditRegular,
  SaveRegular,
  DismissRegular,
} from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import { getAccessTokenClaims } from "@packages/api";
import { useCall } from "../api/useCall";
import { useCallContent } from "../api/useCallContent";
import { useInstructionSet } from "../api/useInstructionSet";
import { useTranscriptSession } from "../api/useTranscriptSession";
import CallInfoTab from "./CallInfoTab";
import ExportPdfMenu from "./ExportPdfMenu";
import InstructionsTab from "./InstructionsTab";
import ResourcesTab from "./ResourcesTab";
import TranscriptTab from "./TranscriptTab";
import type { CallContentDto, CallParticipant, ContentResource } from "../types";
import { idFromUri, schemeFromUri } from "../utils";
import { ResourceSchemeProvider, SharableLinkProvider } from "./ResourceSchemeContext";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    rowGap: tokens.spacingVerticalXS,
  },
  nav: {
    minWidth: 0,
    overflow: "auto",
  },
  toolbar: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalM,
    flexShrink: 0,
  },
  tabContent: {
    flex: 1,
    overflow: "auto",
    padding: tokens.spacingHorizontalL,
  },
  center: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
  },
});

/** @internal Exported for testing */
export function processContent(content: CallContentDto | undefined) {
  if (!content)
    return {
      images: [],
      videos: [],
      stepImages: {} as Record<string, string[]>,
      fileIdToResourceId: {} as Record<string, number>,
      fileIdToScheme: {} as Record<string, string>,
      fileIdToSharableLink: {} as Record<string, string>,
      comment: undefined as string | undefined,
    };

  const images: string[] = [];
  const videos: string[] = [];
  const stepImages: Record<string, string[]> = {};
  const fileIdToResourceId: Record<string, number> = {};
  const fileIdToScheme: Record<string, string> = {};
  const fileIdToSharableLink: Record<string, string> = {};
  let comment: string | undefined;

  content.resources?.forEach((resource: ContentResource) => {
    const fileId = idFromUri(resource.uri);
    fileIdToResourceId[fileId] = resource.id;
    fileIdToScheme[fileId] = schemeFromUri(resource.uri);
    if (resource.sharableLink) {
      fileIdToSharableLink[fileId] = resource.sharableLink;
    }
    if (resource.type === "image") {
      images.push(fileId);
      for (const stepId of resource.stepIds) {
        const key = String(stepId);
        if (!stepImages[key]) stepImages[key] = [];
        stepImages[key].push(fileId);
      }
    } else if (resource.type === "video") {
      videos.push(fileId);
    }
  });

  const nullComment = content.comments?.find((c) => c.stepId === null);
  if (nullComment) comment = nullComment.comment;

  return { images, videos, stepImages, fileIdToResourceId, fileIdToScheme, fileIdToSharableLink, comment };
}

export default function CallDetailView({ callId }: { callId: number }) {
  const styles = useStyles();
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState<string>("info");
  const [isEditing, setIsEditing] = useState(false);
  const [saveSignal, setSaveSignal] = useState(0);
  const [resetSignal, setResetSignal] = useState(0);

  const claims = getAccessTokenClaims();
  const domainId: number = claims?.xm_domain_id ?? 0;
  const domainName: string | undefined = claims?.xm_domain_name;
  const userId = claims?.sub != null ? Number(claims.sub) : null;
  const userRole = claims?.role ?? "guest";

  const callQuery = useCall(callId);
  const call = callQuery.data;

  const isParticipant =
    userId != null &&
    call?.participants?.some((p: CallParticipant) => p.user.id === userId) === true;
  const canEdit = userRole !== "guest" && isParticipant;

  const contentId = call?.content?.id;
  const contentQuery = useCallContent(callId, contentId);
  const content = contentQuery.data ?? call?.content;

  const instructionSetQuery = useInstructionSet(
    domainName,
    content?.instructionSetId ?? undefined,
    content?.instructionSetVersion ?? undefined,
  );
  const instructionSet = instructionSetQuery.data;

  const meetingId = call?.meeting?.id;
  const transcriptQuery = useTranscriptSession(callId, meetingId);
  const transcriptSession = transcriptQuery.data;

  if (callQuery.isLoading) {
    return (
      <div className={styles.center}>
        <Spinner label={t('loadingCall')} />
      </div>
    );
  }

  if (callQuery.error || !call) {
    return (
      <div className={styles.center} style={{ flexDirection: 'column', gap: tokens.spacingVerticalM }}>
        <MessageBar intent="error">
          <MessageBarBody>{t('failedToLoadCall')}</MessageBarBody>
        </MessageBar>
        <Button appearance="primary" onClick={() => callQuery.refetch()}>
          {t('retry')}
        </Button>
      </div>
    );
  }

  const { images, videos, stepImages, fileIdToResourceId, fileIdToScheme, fileIdToSharableLink, comment } = processContent(content);
  const transcript = transcriptSession?.transcripts?.[0];
  const analysis = transcriptSession?.transcriptAnalyses?.[0];
  const hasTranscript = !!transcript && transcript.segments?.length > 0;

  const handleSave = () => {
    setSaveSignal((s) => s + 1);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setResetSignal((s) => s + 1);
    setIsEditing(false);
  };

  const tabs: Array<{
    value: string;
    label: string;
    content: React.ReactNode;
  }> = [
    {
      value: "info",
      label: t('details'),
      content: (
        <CallInfoTab
          call={call}
          analysis={analysis}
          callComment={comment}
          participants={call.participants}
          isEditing={isEditing}
          saveSignal={saveSignal}
          resetSignal={resetSignal}
          callId={callId}
          contentId={contentId}
          meetingId={meetingId}
          content={content}
        />
      ),
    },
  ];

  if (images.length > 0 || videos.length > 0) {
    tabs.push({
      value: "resources",
      label: t('resources'),
      content: (
        <ResourcesTab
          images={images}
          videos={videos}
          domainId={domainId}
          callId={callId}
          contentId={contentId}
          fileIdToResourceId={fileIdToResourceId}
          isEditing={isEditing}
          saveSignal={saveSignal}
          resetSignal={resetSignal}
        />
      ),
    });
  }

  if (hasTranscript) {
    tabs.push({
      value: "transcript",
      label: t('transcript'),
      content: (
        <TranscriptTab
          segments={transcript.segments}
          participants={call.participants}
          isEditing={isEditing}
          saveSignal={saveSignal}
          resetSignal={resetSignal}
          transcriptId={transcript.id}
          callId={callId}
          meetingId={meetingId}
        />
      ),
    });
  }

  if (instructionSet?.steps) {
    const appendixImages = stepImages["null"] ?? [];
    tabs.push({
      value: "instructions",
      label: instructionSet.name,
      content: (
        <InstructionsTab
          steps={instructionSet.steps}
          stepImages={stepImages}
          allImageIds={images}
          comments={content?.comments ?? []}
          completedActionIds={content?.completedActionIds ?? []}
          appendixImageIds={appendixImages}
          domainId={domainId}
          isEditing={isEditing}
          saveSignal={saveSignal}
          resetSignal={resetSignal}
          callId={callId}
          contentId={contentId}
          content={content}
        />
      ),
    });
  }

  const activeTab = tabs.find((t) => t.value === selectedTab) ?? tabs[0];

  return (
    <ResourceSchemeProvider value={fileIdToScheme}>
    <SharableLinkProvider value={fileIdToSharableLink}>
      <main className={styles.root}>
        <header className={styles.header}>
          <nav className={styles.nav} aria-label={t('details')}>
            <TabList
              selectedValue={activeTab.value}
              onTabSelect={(_, data) => setSelectedTab(data.value as string)}
            >
              {tabs.map((tab) => (
                <Tab key={tab.value} value={tab.value}>
                  {tab.label}
                </Tab>
              ))}
            </TabList>
          </nav>
          <div className={styles.toolbar}>
            {!isEditing && (
              <ExportPdfMenu
                callId={callId}
                call={call}
                content={content}
                instructionSet={instructionSet}
                transcriptSession={transcriptSession}
                domainId={domainId}
                hasTranscript={hasTranscript}
                hasImages={images.length > 0}
              />
            )}
            {canEdit && (
              isEditing ? (
                <>
                  <Button
                    appearance="primary"
                    icon={<SaveRegular />}
                    onClick={handleSave}
                  >
                    {t('save')}
                  </Button>
                  <Button
                    appearance="subtle"
                    icon={<DismissRegular />}
                    onClick={handleCancel}
                  >
                    {t('cancel')}
                  </Button>
                </>
              ) : (
                <Button
                  appearance="subtle"
                  icon={<EditRegular />}
                  onClick={() => setIsEditing(true)}
                >
                  {t('edit')}
                </Button>
              )
            )}
          </div>
        </header>
        {tabs.map((tab) => (
          <div
            key={tab.value}
            className={styles.tabContent}
            style={tab.value !== activeTab.value ? { display: 'none' } : undefined}
          >
            {tab.content}
          </div>
        ))}
      </main>
    </SharableLinkProvider>
    </ResourceSchemeProvider>
  );
}
